<template>
  <div style="position: relative;">
    <EditorContent
      :editor="editor"
      :class="[inputClass]"
      class="c-input" />

    <EmojiDropdown
      ref="emojiDropdown"
      :show="showEmojiDropdown"
      :query="emojiQuery"
      :position="dropdownPosition"
      @select="insertDropDownEmoji" />
  </div>
</template>

<script setup>
import { Document } from '@tiptap/extension-document'
import { HardBreak } from '@tiptap/extension-hard-break'
import { History } from '@tiptap/extension-history'
import { Image } from '@tiptap/extension-image'
import { Paragraph } from '@tiptap/extension-paragraph'
import { Placeholder } from '@tiptap/extension-placeholder'
import { Text } from '@tiptap/extension-text'
import { Editor, EditorContent, Extension } from '@tiptap/vue-2'
import {
  onBeforeUnmount, onMounted, ref, watch, nextTick,
} from 'vue'

import EmojiDropdown from './EmojiDropdown.vue'

import { loadEmojiData } from '@/logic/emoji/emojiLoader'
import helpers from '@/logic/helpers'

let emojiData = null
const editor = ref(null)
const emojiDropdown = ref(false)
const showEmojiDropdown = ref(false)
const dropdownPosition = ref({ top: '0px', left: '0px' })
const emojiQuery = ref('')

// Extension to handle emoji shortcode conversion
const EmojiShortcodeExtension = Extension.create({
  name: 'emojiShortcode',

  addInputRules() {
    return [
      {
        find: /:([^:\s]+):/g,
        handler: ({ state, range, match }) => {
          const shortcode = match[0]
          const emojiId = shortcode.replace(/:/g, '')
          const item = emojiData?.emojis?.[emojiId]
          if (!item) {
            return null
          }

          // Standard unicode emoji
          const native = item?.skins?.[0]?.native
          if (native) {
            const { tr } = state
            tr.replaceWith(range.from, range.to, state.schema.text(native))
            return tr
          }

          // Custom image emoji
          const src = item?.skins?.[0]?.src
          if (src && state.schema.nodes.image) {
            const { tr } = state
            const node = state.schema.nodes.image.create({ src, alt: `:${emojiId}:` })
            tr.replaceWith(range.from, range.to, node)
            return tr
          }
          return null
        },
      },
    ]
  },

  addPasteRules() {
    return [
      {
        find: /:([^:\s]+):/g,
        handler: ({ state, range, match }) => {
          const shortcode = match[0]
          const emojiId = shortcode.replace(/:/g, '')
          const item = emojiData?.emojis?.[emojiId]
          if (!item) return null

          const native = item?.skins?.[0]?.native
          if (native) {
            const { tr } = state
            tr.replaceWith(range.from, range.to, state.schema.text(native))
            return tr
          }
          const src = item?.skins?.[0]?.src
          if (src && state.schema.nodes.image) {
            const { tr } = state
            const node = state.schema.nodes.image.create({ src, alt: `:${emojiId}:` })
            tr.replaceWith(range.from, range.to, node)
            return tr
          }
          return null
        },
      },
    ]
  },
})

// To handle Pressing `Enter` when emoji dropdown is opened
const CustomKeymap = Extension.create({
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        // If emoji dropdown is open, select emoji
        if (this.options.showEmojiDropdown.value) {
          this.options.handleEnterKey()
          return true
        }

        const isMobileTouchDevice = helpers.isMobileTouchDevice()

        // Desktop: Enter sends message
        // Mobile/tablet touch devices: Enter adds line break
        if (isMobileTouchDevice) {
          // On mobile/tablet touch devices, Enter should add a hard break (line break)
          return this.editor.commands.setHardBreak()
        }

        // On desktop (including hybrid desktops with touch), Enter should send message
        this.options.handleEnterKey()
        return true
      },
      'Shift-Enter': () => {
        // If emoji dropdown is open, don't interfere
        if (this.options.showEmojiDropdown.value) {
          return false
        }

        const isMobileTouchDevice = helpers.isMobileTouchDevice()

        // Desktop: Shift+Enter adds line break
        // Mobile/tablet touch devices: Shift+Enter sends message (if they have physical keyboard connected)
        if (isMobileTouchDevice) {
          // On mobile/tablet touch devices, Shift+Enter should send message
          this.options.handleEnterKey()
          return true
        }

        // On desktop (including hybrid desktops with touch), Shift+Enter should add a hard break (line break)
        return this.editor.commands.setHardBreak()
      },
    }
  },
})

// Add explicit Ctrl+Z / Ctrl+Y support for undo
const UndoRedoKeymap = Extension.create({
  name: 'undoRedoKeymap',
  // Ensure this keymap runs before others
  priority: 1000,
  addKeyboardShortcuts() {
    return {
      'Shift-Mod-z': () => {
        nextTick(() => {
          editor.value.commands.redo()
        })
        return true
      },
      'Mod-y': () => {
        nextTick(() => {
          editor.value.commands.redo()
        })
        return true
      },
      // 'Mod-z' maps to:
      // Ctrl+Z on Windows and Linux/Ubuntu
      // Cmd+Z on macOS
      'Mod-z': () => {
        nextTick(() => {
          editor.value.commands.undo()
        })
        return true
      },
    }
  },
})

const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  inputClass: {
    type: String,
    default: '',
  },
  enterKeyHandler: {
    type: Function,
    default: () => {},
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['input', 'paste-file'])

const insertDropDownEmoji = (payload) => {
  const { selection } = editor.value.state
  const { from } = selection

  // Match the search query
  const textBeforeCursor = editor.value.state.doc.textBetween(0, from, '\n', '\n')
  const match = textBeforeCursor.match(/:([^:\s]+)$/)
  if (match) {
    const startPos = from - match[0].length // Calculate the start position of the query

    // Delete the query text
    editor.value.chain()
      .focus()
      .deleteRange({ from: startPos, to: from })
      .run()

    // Insert selected emoji
    if (typeof payload === 'string') {
      // Backward compatibility: string assumed to be unicode
      editor.value.chain().focus().insertContent(payload).run()
    } else if (payload?.type === 'native') {
      editor.value.chain().focus().insertContent(payload.value).run()
    } else if (payload?.type === 'image' && payload.value) {
      editor.value.chain().focus().setImage({ src: payload.value, alt: `:${payload.id}:` }).run()
    } else if (payload?.type === 'shortcode') {
      editor.value.chain().focus().insertContent(payload.value).run()
    }
  }

  showEmojiDropdown.value = false
  emojiQuery.value = ''
}

const handleEnterKey = () => {
  if (showEmojiDropdown.value) {
    emojiDropdown.value?.selectEmoji()
  } else {
    props.enterKeyHandler?.()
  }
}

const handleClickOutside = (event) => {
  if (!emojiDropdown.value?.$el?.contains(event.target)) {
    showEmojiDropdown.value = false
  }
}

const setDropdownPosition = (editorInstance) => {
  const { from } = editorInstance.state.selection
  const startCoords = editorInstance.view.coordsAtPos(from)
  const editorCoords = editorInstance.view.dom.getBoundingClientRect()

  // Calculate the position within the editor
  const left = startCoords.left - editorCoords.left + 10

  dropdownPosition.value = {
    left: `${left}px`,
  }
}

const handleEmojiSearch = (editorInstance) => {
  const selection = editorInstance.state.selection
  const { $from } = selection

  const textBeforeCursor = $from.nodeBefore?.textContent || ''
  const match = textBeforeCursor.match(/:([^:\s]+)$/)

  if (match) {
    const query = match[1].toLowerCase()
    emojiQuery.value = query

    if (query.length > 0) {
      showEmojiDropdown.value = true
      setDropdownPosition(editorInstance)
    } else {
      showEmojiDropdown.value = false
    }
  } else {
    // Clear the query when you enter :D, then you remove the D,
    // the query search will be D if we didn't clear it here, then the
    // emoji dropdown will keep last query passed and not update the position
    // of selected emoji (which should be at start 0)
    emojiQuery.value = ''
    showEmojiDropdown.value = false
  }
}

const handleKeyDown = (event) => {
  if (!showEmojiDropdown.value) {
    if (event.ctrlKey && event.code === 'Space') {
      handleEmojiSearch(editor.value)
    }

    return
  }
  if (event.code === 'Escape' && showEmojiDropdown.value) {
    showEmojiDropdown.value = false
  }
}

watch(() => props.value, (newValue) => {
  if (editor.value && editor.value.getHTML() !== newValue) {
    editor.value.commands.setContent(newValue)
  }
})

watch(() => props.disabled, (isDisabled) => {
  if (editor.value) {
    editor.value.setEditable(!isDisabled)
  }
})

onMounted(async () => {
  emojiData = await loadEmojiData()
  editor.value = new Editor({
    extensions: [
      Document,
      Paragraph,
      Text,
      HardBreak,
      History,
      Image.configure({ inline: true, HTMLAttributes: { class: 'c-emoji__image' } }),
      EmojiShortcodeExtension,
      Placeholder.configure({
        placeholder: () => props.placeholder,
        includeChildren: true,
        showOnlyWhenEditable: false,
        showOnlyCurrent: false,
      }),
      CustomKeymap.configure({
        handleEnterKey,
        showEmojiDropdown,
      }),
      UndoRedoKeymap,
    ],
    content: props.value,
    editable: !props.disabled,
    editorProps: {
      handlePaste: (view, event) => {
        const files = []
        const clipboardData = event.clipboardData || window.clipboardData
        if (clipboardData) {
          if (clipboardData.files && clipboardData.files.length) {
            for (let i = 0; i < clipboardData.files.length; i += 1) {
              const clipboardFile = clipboardData.files[i]
              if (clipboardFile && clipboardFile.size > 0) {
                files.push(clipboardFile)
              }
            }
          }
          // Some browsers populate only items with a file-type entry (screenshot/image), while files remains empty.
          // We filter items by item.kind === 'file' and call getAsFile().
          if (clipboardData.items && clipboardData.items.length && !clipboardData.files.length) {
            for (let i = 0; i < clipboardData.items.length; i += 1) {
              const item = clipboardData.items[i]
              if (item && item.kind === 'file') {
                const clipboardItemFile = item.getAsFile()
                if (clipboardItemFile && clipboardItemFile.size > 0) {
                  files.push(clipboardItemFile)
                }
              }
            }
          }
        }

        if (files.length) {
          // We support only single file upload
          emit('paste-file', files[0])
          event.preventDefault()
          return true
        }
        return false
      },
    },
    onUpdate: ({ editor: editorInstance }) => {
      emit('input', editorInstance.isEmpty ? null : editorInstance.getHTML())
      handleEmojiSearch(editorInstance)
    },
  })
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
  if (editor.value) {
    editor.value.destroy()
  }
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeyDown)
})

defineExpose({
  editor,
})
</script>

<style scoped>
/* Ensure placeholder from @tiptap/extension-placeholder is visible */
::v-deep .ProseMirror p.is-editor-empty:first-child::before,
::v-deep .ProseMirror p.is-empty:first-child::before {
  content: attr(data-placeholder);
  pointer-events: none;
  height: 0;
  float: left;
  opacity: 0.6;
}
</style>
